import {
  Heading,
  HStack,
  PageContainer,
  PageHeader,
  Spread,
  Surface,
  Text,
  VStack,
} from '@/components/Atoms'
import { Card, CardContent } from '@/components/Card'
import { PageSize } from '@/components/DataTable/PageSizeSelector'
import { Spinner } from '@/components/Spinner'
import {
  useCustomerPopulationFilters,
  type PopulationDrafts,
} from '@/hooks/filters/credit/useCustomerPopulationFilters'
import {
  useCreateMassClrRun,
  useMassClrPreparerEligibility,
  useMassClrSelectionPreview,
  type MassClrSelectionFilters,
} from '@/hooks/queries/credit/useMassClr'
import { useLocale } from '@/hooks/useLocale'
import { formatCompactCurrency } from '@/lib/utils/numbers'
import { parsePastedErps } from '@/lib/utils/pastedErps'
import {
  EMPTY_MASS_CLR_SELECTION,
  hasAnyPopulationFilter,
  populationSearchSchema,
  toMassClrSelectionFilters,
} from '@/lib/utils/populationFilters'
import { CLR_STRATEGIC_ACCOUNT_THRESHOLD, type MassClrSelectionSortField } from '@repo/constants'
import {
  Button,
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  Field,
  FieldLabel,
  Input,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Textarea,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@repo/ui/components'
import { cn } from '@repo/ui/lib/utils'
import { ColumnSort } from '@tanstack/react-table'
import { createFileRoute, Link, useLocation } from '@tanstack/react-router'
import { ChevronDownIcon, ClipboardIcon, ListFilterIcon, TriangleAlertIcon } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { MassClrSelectionTable } from './-components/MassClrSelectionTable'
import { CREDIT_ROUTES, toStaticData } from '../../credit-route-config'

/**
 * "Create Draft Run". Disabled for several reasons, but only one of them is worth explaining — so
 * the tooltip appears only when the viewer is not a mass preparer, and stays silent when the button
 * is merely waiting on a name or a selection.
 */
function CreateRunButton(props: {
  canPrepare: boolean | undefined
  isDisabled: boolean
  isCreating: boolean
  onClick: () => void
}) {
  const { t } = useTranslation('credit')

  const button = (
    <Button type="button" disabled={props.isDisabled} onClick={props.onClick}>
      {props.isCreating && <Spinner />}
      {t('massClr.select.create')}
    </Button>
  )

  if (props.canPrepare !== false) return button

  return (
    <TooltipProvider delayDuration={150}>
      <Tooltip>
        {/* Radix swallows pointer events on disabled buttons, so the span carries the hover. */}
        <TooltipTrigger asChild>
          <span className="inline-flex">{button}</span>
        </TooltipTrigger>
        <TooltipContent>{t('clr.errors.massClrNotEligiblePreparer')}</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  )
}

export const Route = createFileRoute('/_auth/_modules/credit/credit-limit-reviews/mass/new')({
  component: RouteComponent,
  staticData: toStaticData(CREDIT_ROUTES['/credit/credit-limit-reviews/mass/new']),
  // The entry seed only: the Customers page's "start a mass review" action arrives with its
  // applied filters in the URL. The page never writes back — its drafts stay local.
  validateSearch: (search) => populationSearchSchema.parse(search),
})

/** What the page restores when the user comes back to it. */
type NewRunPageState = {
  drafts: PopulationDrafts
  keyword: string
  applied: MassClrSelectionFilters
  page: number
  pageSize: PageSize
  sort: ColumnSort | undefined
  runName: string
}

/**
 * The page's state per browser history entry, so Back restores the selection (phase 2 UAT: the
 * "Already in another review" link left the page, and returning wiped a pasted list).
 *
 * Keyed by the router's `__TSR_key`: every navigation pushes an entry with a fresh key, while Back
 * and Forward return to the same entry and key. So a visit from the sidebar, the inbox or a typed
 * URL starts empty, and only a return restores. Held in memory on purpose — not session storage,
 * not the URL (an ERP list can run to thousands) — so a refresh starts empty too. Capped, so a
 * long session does not keep every visit's list alive.
 */
const savedPageStates = new Map<string, NewRunPageState>()
const MAX_SAVED_PAGE_STATES = 10

function RouteComponent() {
  const { t } = useTranslation(['credit', 'common'])
  const locale = useLocale()
  const navigate = Route.useNavigate()

  const search = Route.useSearch()
  const historyKey = useLocation({ select: (location) => location.state.__TSR_key })
  // Read once: the state below seeds from it on mount, and later renders keep their own.
  const [restored] = useState(() => (historyKey ? savedPageStates.get(historyKey) : undefined))

  // Filter panel drafts apply on "Show Results" (the applied snapshot is what the preview reads).
  // Same hook as the Customers list — the two screens must always speak the same population
  // vocabulary. Seeded from the URL so a deep link from the Customers page lands with its filters
  // already in the panel AND applied, previewing on arrival; nothing is created until the button.
  const [keyword, setKeyword] = useState(restored?.keyword ?? search.keyword ?? '')
  const population = useCustomerPopulationFilters(restored?.drafts ?? search)
  const [applied, setApplied] = useState<MassClrSelectionFilters>(
    () => restored?.applied ?? toMassClrSelectionFilters(search),
  )

  const [page, setPage] = useState(restored?.page ?? 1)
  const [pageSize, setPageSize] = useState<PageSize>(restored?.pageSize ?? 15)
  const [sort, setSort] = useState<ColumnSort | undefined>(restored?.sort)

  const [runName, setRunName] = useState(restored?.runName ?? '')

  // Set once a run is created: Back from the new run must not reopen the same selection, ready to
  // build a duplicate draft (drafts hold no accounts, so nothing else would stop it).
  const isRunCreatedRef = useRef(false)

  // Every render, so the entry always holds what is on screen when the user leaves. Re-inserting
  // moves the key to the end, which makes the cap drop the least recently used entry.
  useEffect(() => {
    if (!historyKey || isRunCreatedRef.current) return
    savedPageStates.delete(historyKey)
    savedPageStates.set(historyKey, {
      drafts: population.selected,
      keyword,
      applied,
      page,
      pageSize,
      sort,
      runName,
    })
    if (savedPageStates.size > MAX_SAVED_PAGE_STATES) {
      savedPageStates.delete(savedPageStates.keys().next().value!)
    }
  })
  const [isPasteOpen, setIsPasteOpen] = useState(false)
  const [pasteText, setPasteText] = useState('')

  const hasAnyFilter = hasAnyPopulationFilter(applied)

  const previewQuery = useMassClrSelectionPreview(
    applied,
    {
      page,
      pageSize,
      sortField: sort ? (sort.id as MassClrSelectionSortField) : undefined,
      sortDesc: sort?.desc,
    },
    hasAnyFilter,
  )
  const createMutation = useCreateMassClrRun()

  const preview = hasAnyFilter ? previewQuery.data : undefined

  /**
   * Commits the draft filters, which is what the preview actually reads.
   *
   * Deliberately takes NO parameters. It is handed straight to a Button's `onClick`, so React passes
   * the click event as the first argument at runtime — and a signature that accepted anything there
   * would spread the event into the applied filters, putting the button's DOM node into the React
   * Query key and crashing its `JSON.stringify` on the circular fiber. TypeScript cannot catch that:
   * a one-optional-parameter function is assignable to `() => void`. Callers that need to override a
   * value build the object themselves instead.
   */
  const showResults = () => {
    setPage(1)
    setApplied(population.toSelection(keyword))
  }

  const resetAllFilters = () => {
    setKeyword('')
    population.clear()
    setPage(1)
    setApplied(EMPTY_MASS_CLR_SELECTION)
  }

  const pastedErps = parsePastedErps(pasteText)

  /**
   * Pasting a list applies it, rather than only seeding the ERP filter's draft. The draft is not
   * what the preview reads, so the previous behaviour left the page on its "no filters yet" message
   * with the pasted accounts invisible until the user happened to press Show results — pasting
   * looked like it had done nothing at all.
   */
  const applyPaste = () => {
    const mergedErps = [...new Set([...(population.selected.erp ?? []), ...pastedErps])]
    population.set({ ...population.selected, erp: mergedErps })
    // Built here rather than passed to `showResults`: React state is not readable in the tick it is
    // set, so the merged list has to go in directly.
    setPage(1)
    setApplied({ ...population.toSelection(keyword), erp: mergedErps })
    setPasteText('')
    setIsPasteOpen(false)
  }

  /**
   * Creating a run snapshots every selected account — up to 10,000 of them — so the request is not
   * instant. Until it returns there is no run id to navigate to, so the page has to hold still
   * rather than redirect: the whole selection column goes inert and the name field disables, which
   * stops a filter or a paste being changed under a population that is already being written.
   */
  const isCreating = createMutation.isPending

  // Preparer authority does not depend on the run's contents, so the answer is known before
  // anything is selected. `undefined` while the query is in flight is deliberately NOT treated as
  // ineligible: a button that looks refused during load is worse than one that isn't.
  const canPrepare = useMassClrPreparerEligibility().data?.canPrepare
  const cannotPrepare = canPrepare === false

  const createRun = () => {
    createMutation.mutate(
      { name: runName.trim(), filters: applied },
      {
        onSuccess: (created) => {
          isRunCreatedRef.current = true
          if (historyKey) savedPageStates.delete(historyKey)
          navigate({
            to: '/credit/credit-limit-reviews/mass/$runId',
            params: { runId: String(created.id) },
          })
        },
      },
    )
  }

  const pasteDialog = (
    <Dialog open={isPasteOpen} onOpenChange={setIsPasteOpen}>
      <DialogTrigger asChild>
        <Button type="button" variant="secondary">
          <ClipboardIcon />
          {t('massClr.select.paste.trigger')}
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('massClr.select.paste.title')}</DialogTitle>
          <DialogDescription>{t('massClr.select.paste.description')}</DialogDescription>
        </DialogHeader>
        <VStack gap="sm" align="stretch">
          <Textarea
            rows={8}
            value={pasteText}
            onChange={(event) => setPasteText(event.target.value)}
            placeholder={'50316657\n50317320\n50317321'}
            className="font-mono text-xs"
          />
          <Text size="xs" tone="muted">
            {pastedErps.length
              ? t('massClr.select.paste.ready', { count: pastedErps.length })
              : t('massClr.select.paste.empty')}
          </Text>
        </VStack>
        <DialogFooter>
          <Button type="button" variant="secondary" onClick={() => setIsPasteOpen(false)}>
            {t('actions.cancel', { ns: 'common' })}
          </Button>
          <Button type="button" disabled={!pastedErps.length} onClick={applyPaste}>
            {t('massClr.select.paste.add')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )

  // Above-the-table note: how to get started before any filter is applied, and the pasted-ERP
  // match report once the ERP filter is in play.
  const contentSlot = !hasAnyFilter ? (
    <Surface tone="level-2" radius="md" className="flex w-full items-start gap-2.5 border p-3">
      <ListFilterIcon className="text-muted-foreground mt-0.5 size-4 shrink-0" />
      <VStack gap="none">
        <Text size="xs" weight="medium">
          {t('massClr.select.noFiltersTitle')}
        </Text>
        <Text size="xs" tone="muted">
          {t('massClr.select.noFiltersSub')}
        </Text>
      </VStack>
    </Surface>
  ) : applied.erp.length > 0 && preview?.erpMatchInfo ? (
    <Text size="xs" tone="muted">
      {t('massClr.select.erpMatched', {
        matched: preview.erpMatchInfo.matched.toLocaleString(),
        requested: preview.erpMatchInfo.requested.toLocaleString(),
      })}
    </Text>
  ) : undefined

  return (
    <PageContainer maxWidth="wide">
      <Spread align="end">
        <VStack gap="sm">
          <PageHeader>{t('massClr.select.pageHeader')}</PageHeader>
          <Text tone="muted" className="max-w-2xl text-pretty">
            {t('massClr.select.pageDescription')}
          </Text>
        </VStack>
        <Button type="button" variant="secondary" size="sm" asChild>
          <Link to="/credit/credit-limit-reviews/mass">
            {t('actions.cancel', { ns: 'common' })}
          </Link>
        </Button>
      </Spread>

      <div className="grid w-full items-start gap-6 lg:grid-cols-[minmax(0,1fr)_320px]">
        <VStack
          gap="md"
          align="stretch"
          className={cn('min-w-0', isCreating && 'opacity-60 transition-opacity')}
          inert={isCreating}
          aria-busy={isCreating}
        >
          <Card>
            <CardContent className="pt-6">
              <MassClrSelectionTable
                filters={population.filters}
                queryParams={applied}
                search={{
                  value: keyword,
                  setValue: setKeyword,
                  placeholder: t('massClr.select.searchPlaceholder'),
                  className: 'bg-background',
                }}
                page={page}
                pageSize={pageSize}
                sort={sort}
                isEnabled={hasAnyFilter}
                previewQuery={previewQuery}
                toolbarActions={pasteDialog}
                contentSlot={contentSlot}
                onPageChange={(pageIndex) => setPage(pageIndex + 1)}
                onPageSizeChange={(nextPageSize) => {
                  setPageSize(nextPageSize)
                  setPage(1)
                }}
                onSortChange={(nextSort) => {
                  setSort(nextSort)
                  setPage(1)
                }}
                onShowResults={showResults}
                onResetAllFilters={resetAllFilters}
              />
            </CardContent>
          </Card>

          {/* The waterfall from "what you filtered for" down to "what this run will hold". The
              logic is a SEQUENCE of drops, not a list, so it reads as one: every rule the system
              applies, with the number of accounts each one costs. Expanded by default — this is the
              answer to "why is my run smaller than my filter", and it should not need a click. */}
          {preview && preview.matchedCount > 0 && (
            <Card>
              <CardContent className="p-0">
                <Collapsible defaultOpen>
                  <CollapsibleTrigger asChild>
                    <button type="button" className="w-full px-6 py-4 text-left">
                      <Spread align="center">
                        <VStack gap="none">
                          <Text weight="medium">{t('massClr.select.inclusionsTitle')}</Text>
                          <Text size="xs" tone="muted">
                            {t('massClr.select.inclusionsSub')}
                          </Text>
                        </VStack>
                        <ChevronDownIcon className="text-muted-foreground size-4 shrink-0" />
                      </Spread>
                    </button>
                  </CollapsibleTrigger>
                  <CollapsibleContent>
                    <div className="bg-background-level-1 mx-6 mb-6 rounded-md border">
                      <Table>
                        <TableBody>
                          <TableRow>
                            <TableCell>
                              <Text size="xs" weight="medium">
                                {t('massClr.select.inclusion.matched')}
                              </Text>
                            </TableCell>
                            <TableCell className="text-right font-mono text-xs">
                              {preview.matchedCount.toLocaleString()}
                            </TableCell>
                          </TableRow>
                          {preview.exclusions.map((exclusion) => (
                            <TableRow key={exclusion.reason}>
                              <TableCell>
                                <Text size="xs" tone="muted">
                                  {'− '}
                                  {t(`massClr.exclusion.${exclusion.reason}`, {
                                    threshold: formatCompactCurrency(
                                      locale,
                                      CLR_STRATEGIC_ACCOUNT_THRESHOLD,
                                      0,
                                    ),
                                  })}
                                </Text>
                              </TableCell>
                              <TableCell className="text-right font-mono text-xs">
                                {`−${exclusion.count.toLocaleString()}`}
                              </TableCell>
                            </TableRow>
                          ))}
                          <TableRow>
                            <TableCell>
                              <Text size="xs" weight="semibold">
                                {t('massClr.select.inclusion.included')}
                              </Text>
                            </TableCell>
                            <TableCell className="text-right font-mono text-xs font-semibold">
                              {preview.eligibleCount.toLocaleString()}
                            </TableCell>
                          </TableRow>
                          {/*
                            Below the total on purpose: these accounts are IN the run, so the line
                            must not read as another subtraction. It says the $250k rule could not
                            judge them — the difference between "no account breached the cap" and
                            "the cap was never evaluated", which are otherwise identical on screen.
                          */}
                          {preview.unresolvedRecommendationCount > 0 && (
                            <TableRow>
                              <TableCell>
                                <HStack gap="xs" align="center">
                                  <TriangleAlertIcon className="text-warning-text-dark size-3.5 shrink-0" />
                                  <Text size="xs" tone="muted">
                                    {t('massClr.select.inclusion.unresolvedRecommendation')}
                                  </Text>
                                </HStack>
                              </TableCell>
                              <TableCell className="text-right font-mono text-xs">
                                {preview.unresolvedRecommendationCount.toLocaleString()}
                              </TableCell>
                            </TableRow>
                          )}
                        </TableBody>
                      </Table>
                    </div>
                  </CollapsibleContent>
                </Collapsible>
              </CardContent>
            </Card>
          )}

          {preview && preview.exclusions.length > 0 && (
            <Card>
              <CardContent className="p-0">
                <Collapsible>
                  <CollapsibleTrigger asChild>
                    <button type="button" className="w-full px-6 py-4 text-left">
                      <Spread align="center">
                        <VStack gap="none">
                          <Text weight="medium">
                            {t('massClr.select.excluded', {
                              count: preview.matchedCount - preview.eligibleCount,
                            })}
                          </Text>
                          <Text size="xs" tone="muted">
                            {t('massClr.select.excludedSub')}
                          </Text>
                        </VStack>
                        <ChevronDownIcon className="text-muted-foreground size-4 shrink-0" />
                      </Spread>
                    </button>
                  </CollapsibleTrigger>
                  <CollapsibleContent>
                    <div className="bg-background-level-1 mx-6 mb-6 rounded-md border">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>{t('massClr.select.exclusionTable.reason')}</TableHead>
                            <TableHead className="text-right">
                              {t('massClr.select.exclusionTable.accounts')}
                            </TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {preview.exclusions.map((exclusion) => (
                            <TableRow key={exclusion.reason}>
                              <TableCell>
                                <VStack gap="none">
                                  <Text size="xs" weight="medium">
                                    {t(`massClr.exclusion.${exclusion.reason}`, {
                                      threshold: formatCompactCurrency(
                                        locale,
                                        CLR_STRATEGIC_ACCOUNT_THRESHOLD,
                                        0,
                                      ),
                                    })}
                                  </Text>
                                  <Text size="xs" tone="muted">
                                    {t(`massClr.exclusion.${exclusion.reason}Hint`)}
                                  </Text>
                                </VStack>
                              </TableCell>
                              <TableCell className="text-right align-top font-mono text-xs">
                                {exclusion.count.toLocaleString()}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  </CollapsibleContent>
                </Collapsible>
              </CardContent>
            </Card>
          )}
        </VStack>

        <Card className="sticky top-6">
          <CardContent className="pt-6">
            <VStack gap="md" align="stretch">
              <VStack gap="xs">
                <Text size="xs" tone="muted" weight="medium">
                  {t('massClr.select.summaryTitle')}
                </Text>
                <Heading level={2} className="font-mono">
                  {(preview?.eligibleCount ?? 0).toLocaleString()}
                </Heading>
                <Text size="xs" tone="muted">
                  {hasAnyFilter && preview
                    ? t('massClr.select.summaryHint', {
                        matched: preview.matchedCount.toLocaleString(),
                        excluded: (preview.matchedCount - preview.eligibleCount).toLocaleString(),
                      })
                    : t('massClr.select.summaryEmpty')}
                </Text>
              </VStack>
              <Field>
                <FieldLabel htmlFor="mass-clr-run-name">{t('massClr.select.runName')}</FieldLabel>
                <Input
                  id="mass-clr-run-name"
                  value={runName}
                  onChange={(event) => setRunName(event.target.value)}
                  disabled={isCreating}
                />
              </Field>
              <CreateRunButton
                canPrepare={canPrepare}
                isDisabled={
                  cannotPrepare ||
                  !preview ||
                  preview.eligibleCount === 0 ||
                  !runName.trim() ||
                  isCreating
                }
                isCreating={isCreating}
                onClick={createRun}
              />
            </VStack>
          </CardContent>
        </Card>
      </div>
    </PageContainer>
  )
}
